import Moment from 'moment';
import {cloneDeep} from 'lodash';
import {getFilterFuncForKey, keyToFilterField, isInFilter} from './filter_util';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import CreateFilterMutation from '../../../../mutations/create_filter_mutation';
import UpdateFilterMutation from '../../../../mutations/update_filter_mutation';
import DeleteFilterMutation from '../../../../mutations/delete_filter_mutation';
import Util from '../../util/util';
import {groupBy} from 'lodash';
import {BUDGET_TYPE, FILTER_TYPE, GLOBAL_FILTER_OPERATOR} from '../../../../constants';

const shouldFilter = filterCategory => {
	return filterCategory && filterCategory.length !== 0;
};

const taskFilterPredicate = (task, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.task) {
		const filter = filters.task;
		Object.keys(filter).forEach(key => {
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(task, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const projectFilterPredicate = (project, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.project) {
		const filter = filters.project;
		Object.keys(filter).forEach(key => {
			// Workaround that allows us to have the timeoff and internal time fitlers in the project tab
			// skip these 2 filters becase they are handled by the allocationFilterPredicate.
			if ([FILTER_TYPE.INTERNAL_TIME, FILTER_TYPE.TIME_OFF].includes(key)) return;
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(project, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const personFilterPredicate = (person, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.person) {
		const filter = filters.person;
		Object.keys(filter).forEach(key => {
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(person, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const allocationFilterPredicate = (allocation, filters, options) => {
	let filterPredicate = true;
	// use filter.project because we do not have an allocation tab in the filter component
	if (filters && filters.project) {
		const filter = filters.project;
		Object.keys(filter).forEach(key => {
			// only filter these internal time and timeoff
			if (![FILTER_TYPE.INTERNAL_TIME, FILTER_TYPE.TIME_OFF].includes(key)) return;
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(allocation, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const invoiceFilterPredicate = (invoice, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.invoices) {
		const filter = filters.invoices;
		Object.keys(filter).forEach(key => {
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(invoice, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const skillFilterPredicate = (skill, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.skills) {
		const filter = filters.skills;
		Object.keys(filter).forEach(key => {
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(skill, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

const labelFilterPredicate = (label, filters, options) => {
	let filterPredicate = true;
	if (filters && filters.labels) {
		const filter = filters.labels;
		Object.keys(filter).forEach(key => {
			if (shouldFilter(filter[key])) {
				const filterFunc = getFilterFuncForKey(key);
				filterPredicate = filterPredicate && filterFunc(label, filter[key], options);
			}
		});
	}
	return filterPredicate;
};

/*
 * Each filter function can optionally take an options object to supply to the filterFunc. This should ONLY be used for backwards compatibility with old filters.
 * In the future, all new filters should take input: (task, filters).
 * Currently, it is used when supplying a value which is not included in the passed object itself.
 */
export const getFilterFunctions = filters => {
	const taskFilter = (task, options) => taskFilterPredicate(task, filters, options);
	const projectFilter = (project, options) => projectFilterPredicate(project, filters, options);
	const personFilter = (person, options) => personFilterPredicate(person, filters, options);
	const allocationFilter = (allocation, options) => allocationFilterPredicate(allocation, filters, options);
	const invoiceFilter = (invoice, options) => invoiceFilterPredicate(invoice, filters, options);
	const skillFilter = (skill, options) => skillFilterPredicate(skill, filters, options);
	const labelFilter = (label, options) => labelFilterPredicate(label, filters, options);

	return {taskFilter, projectFilter, personFilter, allocationFilter, invoiceFilter, skillFilter, labelFilter};
};

export const removeFilter = (key, k, id, appliedFilters, changeAppliedFilters) => {
	let appliedFiltersCpy = cloneDeep(appliedFilters);
	const idx = appliedFiltersCpy[key][k].findIndex(x => id === x);
	appliedFiltersCpy[key][k].splice(idx, 1);
	changeAppliedFilters(appliedFiltersCpy);
};

export const saveFilters = (filterName, viewerId, filterValues, filterSection, projectId, projectGroupId, callback) => {
	const mutationObject = {
		viewerId: viewerId,
		name: filterName,
		value: JSON.stringify(filterValues),
		section: filterSection ? filterSection : undefined,
		projectId: projectId ? projectId : undefined,
		projectGroupId: projectGroupId ? projectGroupId : undefined,
	};

	Util.CommitMutation(CreateFilterMutation, mutationObject, response => {
		dispatch(EVENT_ID.SAVED_FILTERS_UPDATE, response);
		if (callback) {
			callback(response.createFilter.filter.node.id);
		}
	});
};

export const filtersToArray = storedFilters => {
	if (!storedFilters) {
		return [];
	} else {
		const appliedFilters = [];
		Object.entries(storedFilters).forEach(([section, filterTypes]) => {
			Object.entries(filterTypes).forEach(([filterType, ids]) => {
				ids.forEach(id => {
					appliedFilters.push({section, filterType, id});
				});
			});
		});
		return appliedFilters;
	}
};

export const filtersToObject = filters => {
	if (!filters) {
		return {};
	}
	const localStorageFilters = {};
	const groupedBySection = groupBy(filters, 'section');
	Object.entries(groupedBySection).forEach(([section, filters]) => {
		localStorageFilters[section] = {};
		const groupedByFilterType = groupBy(filters, 'filterType');
		Object.entries(groupedByFilterType).forEach(([filterType, ids]) => {
			localStorageFilters[section][filterType] = ids.map(id => id.id);
		});
	});
	return localStorageFilters;
};

export const renameSavedFilter = (id, name) => {
	Util.CommitMutation(UpdateFilterMutation, {id: id, name: name}, response => {
		dispatch(EVENT_ID.SAVED_FILTERS_UPDATE, response);
	});
};

export const deleteSavedFilter = (id, viewerId) => {
	Util.CommitMutation(DeleteFilterMutation, {id, viewerId}, response => {
		dispatch(EVENT_ID.SAVED_FILTERS_UPDATE, response);
	});
};

/**
 * This is a list of filters that need some conversion before changing it to a "Global" filter.
 *
 * @type {string[]} The the list of filters
 */
const CONVERT_TO_GLOBAL_FILTER = [FILTER_TYPE.PROJECT_TYPE];

/**
 * This method is used to convert a local filter to a global filter.
 *
 * It is used, when there is a need for some manipulation etc.
 *
 * @param key The filter key
 * @param filterValues The filter values
 * @returns {{field: (string|*), value, operator: (string)}} The Global filter
 */
export const convertToGlobalFilter = (key, filterValues) => {
	if (key === FILTER_TYPE.PROJECT_TYPE) {
		const budgetValues = filterValues.map(value => Util.stringToProjectType(value));
		const fixedPrice = budgetValues.filter(value => value === BUDGET_TYPE.FIXED_PRICE).length > 0;
		if (fixedPrice) {
			budgetValues.push(BUDGET_TYPE.FIXED_PRICE_V2);
		}
		let valueString = budgetValues.map(val => val.toString());
		return {
			field: keyToFilterField(key, false),
			operator: isInFilter(key) ? GLOBAL_FILTER_OPERATOR.IN : GLOBAL_FILTER_OPERATOR.IS,
			value: valueString,
		};
	}
};

/**
 * This method will convert local task filters
 * into server side Global filters.
 *
 * Some of them re converted into multiple Global Filters, and some of them
 * is a one-2-one conversion.
 *
 * @param filters The list of task filters
 * @param isProjectGroup Is this from a project group?
 * @returns {*[]} Array of Server Side Global task filters.
 */
export const parseTaskFilters = (filters, isProjectGroup) => {
	const filterValues = [];

	if (filters?.task) {
		for (const [key, value] of Object.entries(filters.task)) {
			if (key === 'indicator_filtered') {
				value.forEach(val => {
					if (val === 'non-billable') {
						const formattedFilter = {
							field: keyToFilterField(val, isProjectGroup),
							operator: GLOBAL_FILTER_OPERATOR.IS_NOT,
							value: ['true'],
						};
						filterValues.push(formattedFilter);
					} else {
						const formattedFilter = {
							field: keyToFilterField(val, isProjectGroup),
							operator: GLOBAL_FILTER_OPERATOR.IS,
							value: ['true'],
						};
						filterValues.push(formattedFilter);
					}
				});
			} else if (key === 'recentActivity' && value.length > 0) {
				let timeVal = Moment().startOf('day').format('YYYY-MM-DD');
				if (value.includes('month')) {
					timeVal = Moment().subtract(1, 'month').startOf('day').format('YYYY-MM-DD');
				} else if (value.includes('week')) {
					timeVal = Moment().subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
				}
				const formattedFilter = {
					field: keyToFilterField(key, isProjectGroup),
					operator: GLOBAL_FILTER_OPERATOR.GREATER_OR_EQUAL,
					value: [timeVal],
				};
				filterValues.push(formattedFilter);
				// If the filter is in the list of filters that needs some conversion - do it.
			} else if (CONVERT_TO_GLOBAL_FILTER.includes(key) && value.length > 0) {
				filterValues.push(convertToGlobalFilter(key, value));
			} else if (value.length > 0) {
				const valueString = value.map(val => val.toString());
				const formattedFilter = {
					field: keyToFilterField(key, isProjectGroup),
					operator: isInFilter(key) ? GLOBAL_FILTER_OPERATOR.IN : GLOBAL_FILTER_OPERATOR.IS,
					value: valueString,
				};
				filterValues.push(formattedFilter);
			}
		}
	}

	return filterValues;
};
